/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * SubregionView — turf-level (subregion) deep dive.
 *
 * Subregions are canonical LD-scoped turf keys (e.g. "12-5"); the DB now
 * carries them on `Voter.subregion` (point-in-polygon) and `Route.subregion`.
 *
 * Fully wired to live data:
 *   - voter stats:   GET /voters/rollup?subregion=<key>   (LD-aware, straddle-safe)
 *   - walking routes: GET /routes/subregion-shifts?subregion=<key>
 * The turf catalog (GET /geo/regions, via MapRegionsContext) supplies only
 * presentational metadata here — friendly name, LD, static map-series PDF
 * link — not the precincts or route data, which the server resolves.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
} from 'recharts';
import { LD_COLORS } from '@/data/dashboardData';
import { useMapRegions } from '@/context/MapRegionsContext';
import type { RouteStopPoint } from '@/data/subregionRouteCoords';
import SubregionRouteMap from '@/components/SubregionRouteMap';
import VoterDetailDrawer from '@/components/VoterDetailDrawer';
import { LD_NAMES } from '@/lib/computeStats';
import { Breadcrumb } from '@/components/shared';
import client from '@/api/client';
import { InfoTip } from '@/components/InfoTip';
import { ColumnLabel } from '@/components/MetricLabel';
import { TierLegend } from '@/components/TierLegend';
import { VisualKey } from '@/components/VisualKey';
import { PrecinctStandouts } from '@/components/PrecinctStandouts';
import { TierPill } from '@/components/TierPill';
import { DeltaChip } from '@/components/DeltaChip';
import { BarMeter } from '@/components/BarMeter';
import { RankBadge } from '@/components/RankBadge';
import {
  useCountyBaselines,
  computePeerBaselines,
  rankWithin,
} from '@/lib/baselines';
import { OnboardingModal, OnboardingHelpButton } from '@/components/OnboardingModal';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { ViewHeader } from '@/components/ViewHeader';

// -------------------------------------------------------------------------
// Types mirroring backend/app/schemas/voter.py → PrecinctGroupDetailResponse
// -------------------------------------------------------------------------

type ElectionHistoryPoint = {
  election_date: string;
  election_type: string;
  voter_count: number;
};

type PrecinctSummary = {
  precinct_num: string;
  precinct_name: string;
  total_voters: number;
  active_pct: number;
  avg_score: number;
  super_voter_pct: number;
  primary_voter_pct: number;
};

type PrecinctGroupDetail = {
  requested_precincts: string[];
  matched_precincts: string[];
  total_voters: number;
  active_voters: number;
  avg_score: number;
  super_voter_pct: number;
  primary_voter_pct: number;
  by_tier: Record<string, number>;
  by_party: Record<string, number>;
  precincts: PrecinctSummary[];
  election_history: ElectionHistoryPoint[];
};

// Live walking-route shifts — mirrors backend SubregionShiftsResponse
// (GET /routes/subregion-shifts). Replaces the static GMAPS +
// SUBREGION_ROUTE_COORDS blobs.
type SubregionShift = {
  id: string;
  route_id: number;
  precinct: string | null;
  doors: number;
  hours: number;
  segs: number;
  url: string | null;
  start: string | null;
  end: string | null;
  stops: RouteStopPoint[];
};

type SubregionShiftsResponse = {
  subregion: string;
  total_shifts: number;
  total_segs: number;
  doors: number;
  turfs: number;
  shifts: SubregionShift[];
};

// -------------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------------

const TIER_DISPLAY_ORDER = ['SUPER_VOTER', 'HOT', 'WARM', 'COOL'];
const TIER_LABELS: Record<string, string> = {
  SUPER_VOTER: 'Super Voter',
  HOT: 'Hot',
  WARM: 'Warm',
  COOL: 'Cool',
};

const PARTY_LABELS: Record<string, string> = {
  R: 'Republican',
  D: 'Democratic',
  I: 'Independent',
  L: 'Libertarian',
  G: 'Green',
  C: 'Constitution',
  UNKNOWN: 'No Party',
};

function toChartData(
  dict: Record<string, number>,
  order?: string[],
  labels?: Record<string, string>,
) {
  const keys = order ?? Object.keys(dict);
  return keys
    .filter((k) => (dict[k] ?? 0) > 0 || order !== undefined)
    .map((k) => ({
      name: labels?.[k] ?? k,
      value: dict[k] ?? 0,
    }));
}

// -------------------------------------------------------------------------
// Shared UI bits (mirrors PrecinctView / LDView)
// -------------------------------------------------------------------------

function Stat({
  label,
  value,
  suffix = '',
  termId,
  children,
}: {
  label: string;
  value: string | number;
  suffix?: string;
  termId?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg bg-bg-raised border border-border-base px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-fg-muted flex items-center">
        <span>{label}</span>
        {termId && <InfoTip termId={termId} compact />}
      </div>
      <div className="mt-0.5 text-xl font-bold text-fg-primary tabular-nums">
        {value}
        {suffix}
      </div>
      {children && <div className="mt-1 space-y-1">{children}</div>}
    </div>
  );
}

// -------------------------------------------------------------------------
// Main view
// -------------------------------------------------------------------------

export const SubregionView: React.FC<any> = ({ sr, onDrill, onBack, onJumpToRoute, focusRoute, onRouteFocusConsumed }) => {
  // Presentational metadata only (friendly name, LD, map PDF) — the voter and
  // route numbers below are all fetched. Undefined while the turf catalog
  // loads and for a turf this tenant does not have; every read is optional.
  const { catalog: regions, loading: regionsLoading } = useMapRegions();
  const srData = regions.byKey[sr];
  const ld = srData?.ld;
  const ldColor = LD_COLORS[ld] || '#dc2626';
  const [forceOnboard, setForceOnboard] = useState(false);

  // Live walking-route shifts for this turf, from the re-seeded DB (replaces
  // the static GMAPS + SUBREGION_ROUTE_COORDS blobs). Best-effort: on failure
  // the routes block simply doesn't render.
  const [shiftsData, setShiftsData] = useState<SubregionShiftsResponse | null>(
    null,
  );
  useEffect(() => {
    let cancelled = false;
    setShiftsData(null);
    client
      .get<SubregionShiftsResponse>(
        `/routes/subregion-shifts?subregion=${encodeURIComponent(sr)}`,
      )
      .then((res) => {
        if (!cancelled) setShiftsData(res.data);
      })
      .catch(() => {
        if (!cancelled) setShiftsData(null);
      });
    return () => {
      cancelled = true;
    };
  }, [sr]);

  const shifts: SubregionShift[] = shiftsData?.shifts ?? [];
  // subregion shift id -> ordered stops, mirroring the old shiftCoords shape.
  const shiftCoords: Record<string, RouteStopPoint[]> = useMemo(
    () => Object.fromEntries(shifts.map((s) => [s.id, s.stops])),
    [shiftsData],
  );
  const shiftIdsWithCoords = Object.keys(shiftCoords);
  const [selectedShiftId, setSelectedShiftId] = useState<string | null>(null);
  const activeShiftId =
    selectedShiftId && shiftCoords[selectedShiftId]
      ? selectedShiftId
      : shiftIdsWithCoords[0] || null;
  const activeShiftIndex = activeShiftId
    ? shiftIdsWithCoords.indexOf(activeShiftId)
    : -1;
  const goToShiftIndex = (i: number) => {
    const n = shiftIdsWithCoords.length;
    if (n === 0) return;
    setSelectedShiftId(shiftIdsWithCoords[((i % n) + n) % n]);
  };

  // --- House-level pagination (inner) — steps through the individual stops
  // of the selected shift, complementing the shift-level carousel (outer).
  const [activeStopIndex, setActiveStopIndex] = useState<number | null>(null);
  const [drawerVoterId, setDrawerVoterId] = useState<number | null>(null);
  const activeStops: any[] =
    (activeShiftId && shiftCoords[activeShiftId]) || [];
  // Guard: when a route-focus request selects a shift AND a stop in the same
  // pass, the shift change would otherwise trip the reset effect below and
  // wipe the freshly-focused stop. We stash the shiftId whose next reset must
  // be skipped exactly once.
  const focusResetGuardShiftId = useRef<string | null>(null);

  // Reset the house pager whenever the selected shift changes — UNLESS a
  // route-focus request just set the stop for this exact shift (skip once).
  useEffect(() => {
    if (focusResetGuardShiftId.current === activeShiftId) {
      focusResetGuardShiftId.current = null;
      return;
    }
    setActiveStopIndex(null);
  }, [activeShiftId]);

  // Route-focus: jump to a specific shift + stop (triggered from a
  // VoterDetailDrawer address click via App.handleJumpToRoute). We set the
  // guard first so the reset effect (keyed on activeShiftId) doesn't clear
  // the stop we're about to set.
  useEffect(() => {
    if (!focusRoute) return;
    const stops = shiftCoords[focusRoute.shiftId];
    if (!stops) return;
    // Only arm the reset-skip guard if selecting this shift will actually
    // change activeShiftId (and thus fire the reset effect). If it's already
    // the active shift, the reset effect won't run, so arming the guard would
    // leave it stale and wrongly skip a future legitimate reset.
    if (activeShiftId !== focusRoute.shiftId) {
      focusResetGuardShiftId.current = focusRoute.shiftId;
    }
    setSelectedShiftId(focusRoute.shiftId);
    if (focusRoute.stopIndex >= 0 && focusRoute.stopIndex < stops.length) {
      setActiveStopIndex(focusRoute.stopIndex);
    }
    onRouteFocusConsumed?.();
  }, [focusRoute]);
  const goHouse = (delta: number) => {
    const n = activeStops.length;
    if (n === 0) return;
    if (activeStopIndex == null) {
      setActiveStopIndex(delta > 0 ? 0 : n - 1);
      return;
    }
    setActiveStopIndex(((activeStopIndex + delta) % n + n) % n);
  };
  const currentStop =
    activeStopIndex != null ? activeStops[activeStopIndex] : null;
  const currentVoterIds: number[] = currentStop?.voter_ids || [];
  const { baselines: countyBl } = useCountyBaselines();
  const reduceMotion = useReducedMotion();

  const [data, setData] = useState<PrecinctGroupDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Wait for the catalog before deciding the turf is unknown — on a cold
    // load `srData` is undefined for a beat and this would otherwise latch an
    // error onto a turf that is about to resolve.
    if (regionsLoading) return;
    if (!srData) {
      setError(`Unknown subregion ${sr}.`);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    // Resolve the turf server-side by its canonical key. The backend applies
    // the LD-aware straddle split (prefers Voter.subregion, falls back to
    // precinct ∩ legislative-district), so the view no longer passes a
    // whole-precinct turf list — which can't express the partial-precinct
    // turfs created where an LD line cuts through a precinct.
    client
      .get<PrecinctGroupDetail>(
        `/voters/rollup?subregion=${encodeURIComponent(sr)}`,
      )
      .then((res) => {
        if (!cancelled) {
          setData(res.data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err?.response?.status;
        if (code === 401)
          setError('Not authenticated — log in to see live subregion data.');
        else setError(err?.message ?? 'Unknown error loading subregion data.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sr, srData, regionsLoading]);

  // ---------- turf catalog still in flight ----------
  // Must precede the "unknown subregion" bail: an empty catalog and a genuinely
  // wrong key look identical, and only one of them is an error.
  if (regionsLoading) {
    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-border-base bg-bg-raised/60 p-12 text-center">
          <div className="inline-block h-3 w-3 rounded-full bg-emerald-500 animate-pulse mr-2" />
          <span className="text-fg-secondary">Loading subregion {sr}...</span>
        </div>
      </div>
    );
  }

  // ---------- missing subregion metadata ----------
  if (!srData) {
    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-status-danger bg-status-danger-bg/40 p-6 text-status-danger">
          <div className="font-semibold mb-1">Unknown subregion "{sr}"</div>
          <div className="text-sm text-status-danger/80">
            This tenant has no turf by that key. If turfs were never loaded
            for this account, contact platform support.
          </div>
        </div>
      </div>
    );
  }

  // ---------- loading state ----------
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-border-base bg-bg-raised/60 p-12 text-center">
          <div className="inline-block h-3 w-3 rounded-full bg-status-success-bg animate-pulse mr-2" />
          <span className="text-fg-secondary">
            Loading subregion {sr} — {srData.name} from Postgres...
          </span>
        </div>
      </div>
    );
  }

  // ---------- error state ----------
  if (error || !data) {
    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-status-danger bg-status-danger-bg/40 p-6 text-status-danger">
          <div className="font-semibold mb-1">
            Couldn't load subregion {sr}
          </div>
          <div className="text-sm text-status-danger/80">{error}</div>
        </div>
      </div>
    );
  }

  // ---------- derived chart data ----------
  const tierData = toChartData(data.by_tier, TIER_DISPLAY_ORDER, TIER_LABELS);
  const partyData = toChartData(data.by_party, undefined, PARTY_LABELS).sort(
    (a, b) => b.value - a.value,
  );
  const activePct = data.total_voters
    ? (data.active_voters * 100) / data.total_voters
    : 0;

  // Precinct-level peer baselines inside this subregion
  const precinctPeers = computePeerBaselines(data.precincts as any);

  const turnoutData = [...data.election_history]
    .sort((a, b) => a.election_date.localeCompare(b.election_date))
    .map((e) => ({
      name: e.election_date.slice(0, 10),
      fullLabel: `${e.election_date} ${e.election_type}`,
      voters: e.voter_count,
      type: e.election_type,
    }));

  // Precinct-comparison chart (avg score per precinct inside this subregion)
  const precinctComparison = data.precincts.map((p) => ({
    name: `P${p.precinct_num}`,
    score: p.avg_score,
  }));

  // Warn if any requested precinct had zero rows in the database
  const missingPrecincts = data.requested_precincts.filter(
    (p) => !data.matched_precincts.includes(p),
  );

  return (
    <div className="space-y-6">
      <Breadcrumb
        items={[
          { label: 'County Map', action: 'map' },
          ld && LD_NAMES[ld]
            ? { label: LD_NAMES[ld], action: () => onDrill('ld', ld) }
            : null,
          {
            label: `${sr} ${srData.name}`,
            action: null,
            mapFile: srData.mapFile,
          },
        ].filter(Boolean)}
        onNavigate={onDrill}
      />

      <OnboardingModal
        viewKey="subregionview"
        title={`Subregion ${sr} — ${srData.name}`}
        intro="A subregion is a turf-assignment grouping of one or more precincts under a friendly name. Use this view to size up the turf, see how its precincts compare on engagement, and grab the walking-route links before a knock shift."
        termIds={[
          'subregion',
          'tier_overview',
          'avg_score',
          'precinct_comparison',
          'turf',
          'super_voter_pct',
        ]}
        workflows={[
          'Print or open the precinct map for this subregion before a shift.',
          'Compare precinct avg scores to decide which doors to prioritize.',
          'Open the Google Maps walking route to send canvassers turn-by-turn.',
          'Click any precinct row to drill into the precinct view.',
        ]}
        forceOpen={forceOnboard}
        onClose={() => setForceOnboard(false)}
      />

      {/* Header strip */}
      <ViewHeader
        title={`${sr} — ${srData.name}`}
        tagline={srData.description}
        inline={
          <>
            <InfoTip termId="subregion" />
          </>
        }
        actions={
          <>
            <OnboardingHelpButton onClick={() => setForceOnboard(true)} />
            <a
              href={`/maps/${srData.mapFile}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-accent hover:bg-accent-hover text-white px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap"
            >
              Precinct Map
            </a>
            <a
              href="/maps/42x42Whole.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-bg-elevated hover:bg-border-subtle text-white px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap"
            >
              County Map
            </a>
            {shifts[0]?.url && (
              <a
                href={shifts[0].url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-status-info-bg hover:bg-status-info-bg/80 text-white px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap"
              >
                Walking Route
              </a>
            )}
          </>
        }
      />

      <TierLegend />
      <VisualKey />

      {/* Standout child precincts — at-a-glance, drills into the precinct */}
      <PrecinctStandouts precincts={data.precincts} onDrill={onDrill} />

      {missingPrecincts.length > 0 && (
        <div className="rounded-lg border border-status-warning/60 bg-status-warning-bg/40 px-4 py-2 text-sm text-status-warning">
          Heads up: precinct(s) {missingPrecincts.join(', ')} defined in the
          turf map have zero voters in the current roll. They were
          skipped in the aggregate below.
        </div>
      )}

      {/* Headline stats (all live) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Total Voters" value={data.total_voters.toLocaleString()} termId="active_voter" />
        <Stat label="Active %" value={activePct.toFixed(1)} suffix="%" termId="active_voter">
          <BarMeter value={activePct} tierColor={false} />
          {countyBl && (
            <DeltaChip
              value={activePct}
              baseline={countyBl.activePct}
              label="vs county"
              format="pct"
              compact
            />
          )}
        </Stat>
        <Stat label="Avg Score" value={data.avg_score.toFixed(1)} termId="avg_score">
          <div className="flex items-center gap-1 flex-wrap">
            <TierPill score={data.avg_score} size="sm" />
            {countyBl && (
              <DeltaChip
                value={data.avg_score}
                baseline={countyBl.avgScore}
                label="vs county"
                format="num"
                compact
              />
            )}
          </div>
          <BarMeter
            value={data.avg_score}
            baseline={countyBl?.avgScore ?? null}
          />
        </Stat>
        <Stat
          label="Super Voter %"
          value={data.super_voter_pct.toFixed(2)}
          suffix="%"
          termId="super_voter_pct"
        >
          {countyBl && (
            <DeltaChip
              value={data.super_voter_pct}
              baseline={countyBl.superVoterPct}
              label="vs county"
              format="pct"
              compact
            />
          )}
          <BarMeter value={data.super_voter_pct} tierColor={false} />
        </Stat>
        <Stat
          label="Primary Voter %"
          value={data.primary_voter_pct.toFixed(1)}
          suffix="%"
          termId="primary_voter"
        >
          {countyBl && (
            <DeltaChip
              value={data.primary_voter_pct}
              baseline={countyBl.primaryVoterPct}
              label="vs county"
              format="pct"
              compact
            />
          )}
          <BarMeter value={data.primary_voter_pct} tierColor={false} />
        </Stat>
        <Stat label="Precincts" value={data.precincts.length} termId="precinct" />
      </div>

      {/* Turnout history */}
      <div className="bg-bg-raised rounded-xl p-6 border border-border-base">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-lg font-bold text-fg-primary flex items-center gap-2">
            Past Election Turnout <InfoTip termId="roster_turnout" />
          </h2>
          <span className="text-xs text-fg-muted">
            Voters from this subregion who cast a ballot · newest{' '}
            {turnoutData.length} elections
          </span>
        </div>
        {turnoutData.length > 0 ? (() => {
          const firstT = turnoutData[0];
          const lastT = turnoutData[turnoutData.length - 1];
          const delta = lastT.voters - firstT.voters;
          const dir = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
          const pct = firstT.voters
            ? ((delta / firstT.voters) * 100).toFixed(1)
            : '0';
          const srLabel = `${sr} ${srData.name}`;
          const shortLabel = `Line chart: past-election turnout for ${srLabel} across ${turnoutData.length} elections, ${dir} ${Math.abs(Number(pct))}% from ${firstT.voters.toLocaleString()} to ${lastT.voters.toLocaleString()} voters.`;
          const longNarrative = `Past election turnout for subregion ${srLabel}: ${turnoutData
            .map((t) => `${t.fullLabel} ${t.voters.toLocaleString()} voters`)
            .join('; ')}. Overall ${dir} ${Math.abs(Number(pct))}% from first to last recorded election.`;
          return (
            <figure aria-label={shortLabel} className="m-0">
              <figcaption className="sr-only">{longNarrative}</figcaption>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={turnoutData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                    angle={-30}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis tick={{ fill: '#94a3b8' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#0f172a',
                      border: '1px solid #334155',
                    }}
                    labelFormatter={(_, payload) =>
                      payload?.[0]?.payload?.fullLabel ?? ''
                    }
                    formatter={(value: number) => [value.toLocaleString(), 'Voters']}
                  />
                  <Line
                    type="monotone"
                    dataKey="voters"
                    stroke={ldColor}
                    strokeWidth={2}
                    dot={{ r: 3, fill: ldColor }}
                    activeDot={{ r: 6 }}
                    isAnimationActive={!reduceMotion}
                  />
                </LineChart>
              </ResponsiveContainer>
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer text-fg-secondary hover:text-fg-primary">
                  Show data as table
                </summary>
                <table className="mt-2 w-full">
                  <thead>
                    <tr>
                      <th className="text-left py-1 px-2 text-fg-muted">Election</th>
                      <th className="text-right py-1 px-2 text-fg-muted">Voters</th>
                    </tr>
                  </thead>
                  <tbody>
                    {turnoutData.map((t) => (
                      <tr key={t.fullLabel} className="border-t border-border-base">
                        <td className="py-1 px-2 text-fg-secondary">{t.fullLabel}</td>
                        <td className="py-1 px-2 text-right text-fg-secondary tabular-nums">
                          {t.voters.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            </figure>
          );
        })() : (
          <div className="text-sm text-fg-muted py-10 text-center">
            No past-election records in this subregion yet.
          </div>
        )}
      </div>

      {/* Tier + Party row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-bg-raised rounded-xl p-6 border border-border-base">
          <h2 className="text-lg font-bold text-fg-primary mb-4 flex items-center gap-2">
            Engagement Tiers <InfoTip termId="tier_overview" />
          </h2>
          {(() => {
            const srLabel = `${sr} ${srData.name}`;
            const top = [...tierData].sort((a, b) => b.value - a.value)[0];
            const total = tierData.reduce((s, d) => s + d.value, 0);
            const topPct = total > 0 && top ? ((top.value / total) * 100).toFixed(1) : '0';
            const shortLabel = top
              ? `Bar chart: engagement tier distribution across ${tierData.length} tiers for ${srLabel}. ${top.name} dominates with ${top.value.toLocaleString()} voters (${topPct}% of the subregion).`
              : `Bar chart: engagement tiers for ${srLabel}. No data.`;
            const longNarrative = `Engagement tiers in ${srLabel}: ${tierData
              .map(
                (d) =>
                  `${d.name} ${d.value.toLocaleString()} voters (${
                    total > 0 ? ((d.value / total) * 100).toFixed(1) : '0'
                  }%)`,
              )
              .join('; ')}.`;
            return (
              <figure aria-label={shortLabel} className="m-0">
                <figcaption className="sr-only">{longNarrative}</figcaption>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={tierData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                    <YAxis tick={{ fill: '#94a3b8' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#0f172a',
                        border: '1px solid #334155',
                      }}
                      formatter={(value: number) => value.toLocaleString()}
                    />
                    <Bar dataKey="value" fill="#dc2626" isAnimationActive={!reduceMotion} />
                  </BarChart>
                </ResponsiveContainer>
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-fg-secondary hover:text-fg-primary">
                    Show data as table
                  </summary>
                  <table className="mt-2 w-full">
                    <thead>
                      <tr>
                        <th className="text-left py-1 px-2 text-fg-muted">Tier</th>
                        <th className="text-right py-1 px-2 text-fg-muted">Voters</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tierData.map((d) => (
                        <tr key={d.name} className="border-t border-border-base">
                          <td className="py-1 px-2 text-fg-secondary">{d.name}</td>
                          <td className="py-1 px-2 text-right text-fg-secondary tabular-nums">
                            {d.value.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              </figure>
            );
          })()}
        </div>

        <div className="bg-bg-raised rounded-xl p-6 border border-border-base">
          <h2 className="text-lg font-bold text-fg-primary mb-4 flex items-center gap-2">
            Party Registration <InfoTip termId="party_component" />
          </h2>
          {(() => {
            const srLabel = `${sr} ${srData.name}`;
            const top = partyData[0];
            const total = partyData.reduce((s, d) => s + d.value, 0);
            const topPct = total > 0 && top ? ((top.value / total) * 100).toFixed(1) : '0';
            const shortLabel = top
              ? `Bar chart: party registration across ${partyData.length} parties for ${srLabel}. ${top.name} leads with ${top.value.toLocaleString()} voters (${topPct}%).`
              : `Bar chart: party registration for ${srLabel}. No data.`;
            const longNarrative = `Party registration in ${srLabel}: ${partyData
              .map(
                (d) =>
                  `${d.name} ${d.value.toLocaleString()} voters (${
                    total > 0 ? ((d.value / total) * 100).toFixed(1) : '0'
                  }%)`,
              )
              .join('; ')}.`;
            return (
              <figure aria-label={shortLabel} className="m-0">
                <figcaption className="sr-only">{longNarrative}</figcaption>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={partyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                    <YAxis tick={{ fill: '#94a3b8' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#0f172a',
                        border: '1px solid #334155',
                      }}
                      formatter={(value: number) => value.toLocaleString()}
                    />
                    <Bar dataKey="value" fill="#2563eb" isAnimationActive={!reduceMotion} />
                  </BarChart>
                </ResponsiveContainer>
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-fg-secondary hover:text-fg-primary">
                    Show data as table
                  </summary>
                  <table className="mt-2 w-full">
                    <thead>
                      <tr>
                        <th className="text-left py-1 px-2 text-fg-muted">Party</th>
                        <th className="text-right py-1 px-2 text-fg-muted">Voters</th>
                      </tr>
                    </thead>
                    <tbody>
                      {partyData.map((d) => (
                        <tr key={d.name} className="border-t border-border-base">
                          <td className="py-1 px-2 text-fg-secondary">{d.name}</td>
                          <td className="py-1 px-2 text-right text-fg-secondary tabular-nums">
                            {d.value.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              </figure>
            );
          })()}
        </div>
      </div>

      {/* Precinct comparison chart (by avg score) */}
      {precinctComparison.length > 0 && (
        <div className="bg-bg-raised rounded-xl p-6 border border-border-base">
          <h2 className="text-lg font-bold text-fg-primary mb-4 flex items-center gap-2">
            Precinct Comparison (avg score) <InfoTip termId="precinct_comparison" />
          </h2>
          {(() => {
            const srLabel = `${sr} ${srData.name}`;
            const sorted = [...precinctComparison].sort((a, b) => b.score - a.score);
            const top = sorted[0];
            const bottom = sorted[sorted.length - 1];
            const shortLabel = `Bar chart: average voter score across ${precinctComparison.length} precincts in ${srLabel}. Highest: ${top.name} at ${top.score.toFixed(1)}; lowest: ${bottom.name} at ${bottom.score.toFixed(1)}.`;
            const longNarrative = `Precinct average scores in ${srLabel}: ${precinctComparison
              .map((d) => `${d.name} score ${d.score.toFixed(1)}`)
              .join('; ')}.`;
            return (
              <figure aria-label={shortLabel} className="m-0">
                <figcaption className="sr-only">{longNarrative}</figcaption>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={precinctComparison}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="name" tick={{ fill: '#94a3b8' }} />
                    <YAxis tick={{ fill: '#94a3b8' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#0f172a',
                        border: '1px solid #334155',
                      }}
                    />
                    <Bar dataKey="score" fill="#059669" isAnimationActive={!reduceMotion} />
                  </BarChart>
                </ResponsiveContainer>
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-fg-secondary hover:text-fg-primary">
                    Show data as table
                  </summary>
                  <table className="mt-2 w-full">
                    <thead>
                      <tr>
                        <th className="text-left py-1 px-2 text-fg-muted">Precinct</th>
                        <th className="text-right py-1 px-2 text-fg-muted">Avg score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {precinctComparison.map((d) => (
                        <tr key={d.name} className="border-t border-border-base">
                          <td className="py-1 px-2 text-fg-secondary">{d.name}</td>
                          <td className="py-1 px-2 text-right text-fg-secondary tabular-nums">
                            {d.score.toFixed(1)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              </figure>
            );
          })()}
        </div>
      )}

      {/* Per-precinct drill-down table */}
      <div className="bg-bg-raised rounded-xl p-6 border border-border-base min-w-0">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-lg font-bold text-fg-primary flex items-center gap-2">
            Precincts in {sr} <InfoTip termId="precinct" />
          </h2>
          <span className="text-xs text-fg-muted">
            {data.precincts.length} precincts · ordered by voter count
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-base">
                <th className="text-left py-2 px-3 text-fg-muted font-semibold">
                  <ColumnLabel termId="precinct">Precinct</ColumnLabel>
                </th>
                <th className="text-right py-2 px-3 text-fg-muted font-semibold">
                  <ColumnLabel termId="active_voter">Total</ColumnLabel>
                </th>
                <th className="text-left py-2 px-3 text-fg-muted font-semibold">
                  <ColumnLabel termId="active_voter">Active %</ColumnLabel>
                </th>
                <th className="text-left py-2 px-3 text-fg-muted font-semibold">
                  <ColumnLabel termId="avg_score">Avg Score</ColumnLabel>
                </th>
                <th className="text-center py-2 px-3 text-fg-muted font-semibold">
                  SR Rank
                </th>
                <th className="text-right py-2 px-3 text-fg-muted font-semibold">
                  <ColumnLabel termId="super_voter_pct">Super Voter %</ColumnLabel>
                </th>
                <th className="text-right py-2 px-3 text-fg-muted font-semibold">
                  <ColumnLabel termId="primary_voter">Primary %</ColumnLabel>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.precincts.map((p) => {
                const scoreRank = rankWithin(
                  data.precincts as any,
                  (r: any) => r.avg_score,
                  p.avg_score,
                  'higher-better',
                );
                return (
                <tr
                  key={p.precinct_num}
                  onClick={() => onDrill('precinct', p.precinct_num)}
                  className="border-b border-border-base hover:bg-bg-elevated cursor-pointer transition"
                >
                  <td className="py-2 px-3 text-fg-primary font-medium">
                    P{p.precinct_num} {p.precinct_name}
                  </td>
                  <td className="py-2 px-3 text-right text-fg-secondary tabular-nums">
                    {p.total_voters.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-left text-fg-secondary min-w-[140px]">
                    <div className="flex items-center gap-1 mb-1">
                      <span className="tabular-nums">
                        {p.active_pct.toFixed(1)}%
                      </span>
                      {countyBl && (
                        <DeltaChip
                          value={p.active_pct}
                          baselines={[
                            { label: 'vs SR', value: activePct },
                            {
                              label: 'peer med',
                              value: precinctPeers.activePct,
                            },
                            { label: 'vs county', value: countyBl.activePct },
                          ]}
                          format="pct"
                          compact
                        />
                      )}
                    </div>
                    <BarMeter
                      value={p.active_pct}
                      baseline={activePct}
                      tierColor={false}
                      height={3}
                    />
                  </td>
                  <td className="py-2 px-3 text-left text-fg-primary min-w-[160px]">
                    <div className="flex items-center gap-1 mb-1 flex-wrap">
                      <span className="font-semibold tabular-nums">
                        {p.avg_score.toFixed(1)}
                      </span>
                      <TierPill score={p.avg_score} size="sm" />
                      <DeltaChip
                        value={p.avg_score}
                        baselines={[
                          { label: 'vs SR', value: data.avg_score },
                          {
                            label: 'peer med',
                            value: precinctPeers.avgScore,
                          },
                          ...(countyBl
                            ? [
                                {
                                  label: 'vs county',
                                  value: countyBl.avgScore,
                                },
                              ]
                            : []),
                        ]}
                        format="num"
                        compact
                      />
                    </div>
                    <BarMeter
                      value={p.avg_score}
                      baseline={data.avg_score}
                      height={3}
                    />
                  </td>
                  <td className="py-2 px-3 text-center">
                    <RankBadge
                      rank={scoreRank.rank}
                      total={scoreRank.total}
                    />
                  </td>
                  <td className="py-2 px-3 text-right text-fg-secondary tabular-nums">
                    {p.super_voter_pct.toFixed(2)}%
                  </td>
                  <td className="py-2 px-3 text-right text-fg-secondary tabular-nums">
                    {p.primary_voter_pct.toFixed(1)}%
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Walking-route block — live from the re-seeded DB via
          GET /routes/subregion-shifts. */}
      {shiftsData && (
        <div className="bg-bg-raised rounded-xl p-6 border border-border-base">
          <h2 className="text-lg font-bold text-fg-primary mb-4">
            Walking Routes
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-bg-base rounded-lg p-4 border border-border-base">
              <div className="text-xs text-fg-muted uppercase">Doors</div>
              <div className="text-2xl font-bold text-fg-primary tabular-nums">
                {shiftsData.doors.toLocaleString()}
              </div>
            </div>
            <div className="bg-bg-base rounded-lg p-4 border border-border-base">
              <div className="text-xs text-fg-muted uppercase">Turfs</div>
              <div className="text-2xl font-bold text-fg-primary tabular-nums">
                {shiftsData.turfs.toLocaleString()}
              </div>
            </div>
            <div className="bg-bg-base rounded-lg p-4 border border-border-base">
              <div className="text-xs text-fg-muted uppercase">Super Voters</div>
              <div className="text-2xl font-bold text-tier-anchor tabular-nums">
                {(data.by_tier?.SUPER_VOTER ?? 0).toLocaleString()}
              </div>
            </div>
            <div className="bg-bg-base rounded-lg p-4 border border-border-base">
              <div className="text-xs text-fg-muted uppercase">
                Hot Targets
              </div>
              <div className="text-2xl font-bold text-tier-hot tabular-nums">
                {(data.by_tier?.HOT ?? 0).toLocaleString()}
              </div>
            </div>
          </div>
          {shifts.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-accent-fg uppercase tracking-wide">
                Clickable Walking Routes
              </h3>

              {/* Responsive: carousel of route maps on the left, shift summary
                  list beside it on large screens (stacked on mobile). */}
              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_22rem] gap-4 items-start">
                {/* In-app route map carousel for the selected shift (Google link
                    kept as a secondary option on each card). */}
                {activeShiftId && shiftCoords[activeShiftId] && (
                  <div className="space-y-2">
                    <div className="relative">
                      <SubregionRouteMap
                        key={activeShiftId}
                        stops={shiftCoords[activeShiftId]}
                        shiftLabel={activeShiftId}
                        activeStopIndex={activeStopIndex}
                        onStopSelect={setActiveStopIndex}
                      />
                      {shiftIdsWithCoords.length > 1 && (
                        <>
                          <button
                            type="button"
                            aria-label="Previous walking route"
                            onClick={() => goToShiftIndex(activeShiftIndex - 1)}
                            className="absolute left-2 top-1/2 -translate-y-1/2 z-[1000] h-12 w-12 flex items-center justify-center rounded-full bg-bg-base/90 border border-border-base text-fg-primary text-xl font-bold shadow-md hover:bg-bg-elevated hover:border-accent/60"
                          >
                            ‹
                          </button>
                          <button
                            type="button"
                            aria-label="Next walking route"
                            onClick={() => goToShiftIndex(activeShiftIndex + 1)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 z-[1000] h-12 w-12 flex items-center justify-center rounded-full bg-bg-base/90 border border-border-base text-fg-primary text-xl font-bold shadow-md hover:bg-bg-elevated hover:border-accent/60"
                          >
                            ›
                          </button>
                        </>
                      )}
                    </div>
                    {shiftIdsWithCoords.length > 1 && (
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-fg-muted whitespace-nowrap">
                          Route {activeShiftIndex + 1} of{' '}
                          {shiftIdsWithCoords.length} · {activeShiftId}
                        </span>
                        <div className="flex items-center gap-1">
                          {shiftIdsWithCoords.map((id, i) => {
                            const active = id === activeShiftId;
                            return (
                              <button
                                key={id}
                                type="button"
                                aria-label={`Show walking route ${i + 1}`}
                                aria-current={active}
                                onClick={() => setSelectedShiftId(id)}
                                className="flex items-center justify-center p-1 min-h-0 leading-none"
                              >
                                {/* Inner span is the visible indicator so the
                                    global field-mode min-height (48px) only
                                    inflates the invisible tap target, not the
                                    dot. Active state expands to a pill. */}
                                <span
                                  className={`block h-1.5 rounded-full transition-all duration-200 ${
                                    active
                                      ? 'w-5 bg-accent'
                                      : 'w-1.5 bg-border-base hover:bg-fg-muted'
                                  }`}
                                />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* House pager (inner pagination) — one compact row that
                        steps through the individual stops of the selected
                        shift, complementing the shift carousel above. Each step
                        focuses the map and opens that door's popup. */}
                    {activeStops.length >= 2 && (
                      <div className="flex items-center gap-2 rounded-lg border border-border-base bg-bg-base px-2 py-1.5">
                        <button
                          type="button"
                          aria-label="Previous house"
                          onClick={() => goHouse(-1)}
                          className="h-9 w-9 shrink-0 flex items-center justify-center rounded-full border border-border-base bg-bg-elevated text-fg-primary text-lg font-bold hover:border-accent/60"
                        >
                          ‹
                        </button>
                        <div className="min-w-0 flex-1 text-sm text-fg-primary truncate">
                          {currentStop ? (
                            <>
                              <span className="font-bold">
                                #{activeStopIndex + 1} · {currentStop.address}
                              </span>
                              <span className="ml-1 text-xs font-normal text-fg-muted">
                                · {currentVoterIds.length} voter
                                {currentVoterIds.length === 1 ? '' : 's'}
                              </span>
                            </>
                          ) : (
                            <span className="text-fg-muted">
                              {activeStops.length} houses — step through ›
                            </span>
                          )}
                        </div>
                        {currentStop && currentVoterIds.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setDrawerVoterId(currentVoterIds[0])}
                            className="shrink-0 rounded-lg bg-accent text-white px-3 py-2 text-sm font-bold whitespace-nowrap hover:bg-accent/90"
                          >
                            {currentVoterIds.length > 1
                              ? `View ${currentVoterIds.length} voters →`
                              : 'View voter →'}
                          </button>
                        )}
                        <button
                          type="button"
                          aria-label="Next house"
                          onClick={() => goHouse(1)}
                          className="h-9 w-9 shrink-0 flex items-center justify-center rounded-full border border-border-base bg-bg-elevated text-fg-primary text-lg font-bold hover:border-accent/60"
                        >
                          ›
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Shift summary — sits beside the maps on lg, capped to the
                    map height (h-80 = 320px) so the columns align. The list
                    scrolls internally; the totals footer is pinned below it so
                    it stays visible no matter how far the list is scrolled. */}
                <div className="lg:max-h-80 lg:flex lg:flex-col">
                  <div className="space-y-3 lg:flex-1 lg:min-h-0 lg:overflow-y-auto lg:pr-1">
                    {shifts.map((shift: SubregionShift, idx: number) => {
                      const hasMap = !!shiftCoords[shift.id];
                      const isActive = shift.id === activeShiftId;
                      return (
                        <div
                          key={idx}
                          onClick={() => hasMap && setSelectedShiftId(shift.id)}
                          className={`bg-bg-base rounded-lg p-4 border flex items-center justify-between gap-3 ${
                            isActive ? 'border-accent' : 'border-border-base'
                          } ${hasMap ? 'cursor-pointer hover:border-accent/60' : ''}`}
                        >
                          <div className="min-w-0">
                            <div className="font-bold text-fg-primary text-sm">
                              {shift.id}
                              {hasMap && (
                                <span className="ml-2 text-xs font-semibold text-accent-fg">
                                  {isActive ? '● shown on map' : 'view on map'}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-fg-muted">
                              {shift.doors} doors · {shift.hours}hrs ·{' '}
                              {shift.precinct}
                            </div>
                            <div className="text-xs text-fg-muted mt-1">
                              {shift.start} → {shift.end}
                            </div>
                          </div>
                          <a
                            href={shift.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="bg-bg-elevated hover:bg-border-subtle text-fg-primary px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap"
                          >
                            Google Maps
                          </a>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pinned totals footer — always visible below the list. */}
                  <div className="mt-3 pt-3 border-t border-border-base flex items-center justify-between gap-3 text-sm">
                    <span className="font-bold text-fg-primary">
                      <span className="text-accent-fg tabular-nums">
                        {shiftsData.total_shifts.toLocaleString()}
                      </span>{' '}
                      shifts available
                    </span>
                    <span className="font-bold text-fg-primary">
                      <span className="text-accent-fg tabular-nums">
                        {shiftsData.total_segs.toLocaleString()}
                      </span>{' '}
                      walking segments
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Drill-down: a house's "View voter details" opens the shared drawer. */}
      <VoterDetailDrawer
        voterId={drawerVoterId}
        onClose={() => setDrawerVoterId(null)}
        onJumpToRoute={onJumpToRoute}
      />
    </div>
  );
};
